<template>
  <div class="w-full mx-auto h-screen">
    <AppLoading
      v-if="vehiclesStore.loading"
      class="mx-auto w-full flex justify-center"
    ></AppLoading>
    <div v-if="settings.viewMode === 'grid'">
      <div
        class="flex flex-wrap justify-center dark:bg-black"
        :style="{ rowGap: settings.itemSetting.verticalGap }"
      >
        <div
          v-for="(vehicle, index) in sortVehicles()"
          :key="index"
          class="flex justify-center"
        >
          <a
            @click="
              disableUrl(vehicle.make, vehicle.model, vehicle.count)
                ? ''
                : goToVehiclePage(vehicle.make, vehicle.model)
            "
            class="jellybeans__card--link"
            :style="{ 'max-width': settings.imageSetting.width + 'px' }"
            :class="{
              'cursor-pointer': !disableUrl(
                vehicle.make,
                vehicle.model,
                vehicle.count,
              ),
            }"
            target="_blank"
          >
            <div>
              <img
                @error="handleError"
                :src="
                  vehicle.trim && vehicle.trimImg && vehicle.trimImg?.length > 0
                    ? vehicle.trimImg
                    : customImage(vehicle.make, vehicle.model)
                      ? customImage(vehicle.make, vehicle.model) || undefined
                      : `${apiUrl}?make=${vehicle.make}&model=${vehicle.model}&category=${settings.imageType}`
                "
                :alt="`${vehicle.make} ${vehicle.model}`"
                width="200"
                loading="lazy"
                class="mx-auto"
              />
            </div>

            <div class="text-center">
              <span
                class="jellybeans__label"
                :style="{
                  color: settings.labelSetting.color,
                  fontSize: settings.labelSetting.typography.fontSize,
                  fontWeight: settings.labelSetting.typography.fontWeight,
                  lineHeight: settings.labelSetting.typography.lineHeight,
                }"
              >
                {{
                  settings.vehicleNameType === 'Model'
                    ? vehicle.model
                    : vehicle.make + ' ' + vehicle.model
                }}
                <span v-if="hideWhenZeroInStock(vehicle.make, vehicle.model)">
                  {{
                    settings.showVehicleCount
                      ? `${getCustomCountText(vehicle.make, vehicle.model) ? getCustomCountText(vehicle.make, vehicle.model) : `(${vehicle.count})`}`
                      : ''
                  }}
                </span>
              </span>
            </div>
          </a>
        </div>
      </div>
    </div>
    <div v-else>
      <Carousel v-bind="carouselConfig">
        <Slide
          v-for="(vehicle, index) in sortVehicles()"
          :key="index"
          class="flex justify-center"
        >
          <div
            class="flex flex-wrap justify-center dark:bg-black"
            :style="{ rowGap: settings.itemSetting.verticalGap }"
          >
            <a
              @click="
                disableUrl(vehicle.make, vehicle.model, vehicle.count)
                  ? ''
                  : goToVehiclePage(vehicle.make, vehicle.model)
              "
              class="jellybeans__card--link"
              :style="{ 'max-width': settings.imageSetting.width + 'px' }"
              :class="{
                'cursor-pointer': !disableUrl(
                  vehicle.make,
                  vehicle.model,
                  vehicle.count,
                ),
              }"
              target="_blank"
            >
              <div>
                <img
                  @error="handleError"
                  :src="
                    vehicle.trim &&
                    vehicle.trimImg &&
                    vehicle.trimImg?.length > 0
                      ? vehicle.trimImg
                      : customImage(vehicle.make, vehicle.model)
                        ? customImage(vehicle.make, vehicle.model) || undefined
                        : `${apiUrl}?make=${vehicle.make}&model=${vehicle.model}&category=${settings.imageType}`
                  "
                  :alt="`${vehicle.make} ${vehicle.model}`"
                  :width="settings.imageSetting.width"
                  loading="lazy"
                  class="mx-auto"
                />
              </div>

              <div class="text-center">
                <span
                  class="jellybeans__label"
                  :style="{
                    color: settings.labelSetting.color,
                    fontSize: settings.labelSetting.typography.fontSize,
                    fontWeight: settings.labelSetting.typography.fontWeight,
                    lineHeight: settings.labelSetting.typography.lineHeight,
                  }"
                >
                  {{
                    settings.vehicleNameType === 'Model'
                      ? vehicle.model
                      : vehicle.make + ' ' + vehicle.model
                  }}
                  <span v-if="hideWhenZeroInStock(vehicle.make, vehicle.model)">
                    {{
                      settings.showVehicleCount
                        ? `${getCustomCountText(vehicle.make, vehicle.model) ? getCustomCountText(vehicle.make, vehicle.model) : `(${vehicle.count})`}`
                        : ''
                    }}
                  </span>
                </span>
              </div>
            </a>
          </div>
        </Slide>
        <template #addons>
          <Navigation class="mx-20" />
        </template>
      </Carousel>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useVehiclesStore } from '@/stores/vehicles'
import { useSettingsStore } from '@/stores/settings'
import { onMounted } from 'vue'
import AppLoading from './AppLoading.vue'
import 'vue3-carousel/carousel.css'
import { Carousel, Slide, Navigation } from 'vue3-carousel'
import novehicleImage from '@/assets/no-vehicle-image.png'

const carouselConfig = {
  itemsToShow: 5,
  itemsToScroll: 1,
  wrapAround: true,
  breakpoints: {
    300: { itemsToShow: 1 },
    640: { itemsToShow: 2 },
    768: { itemsToShow: 4 },
    900: { itemsToShow: 5 },
  },
}

const vehiclesStore = useVehiclesStore()
const settings = useSettingsStore()

const apiUrl = import.meta.env.VITE_VEHICLE_IMAGE_URL
onMounted(async () => {
  if (settings.accountId !== '') {
    await vehiclesStore.getVehicles()
  }
})

const handleError = (event: Event) => {
  ;(event.target as HTMLImageElement).src = novehicleImage
}
const customImage = (make: string, model: string) => {
  const filterArray = settings.filter

  if (Array.isArray(filterArray)) {
    const item = filterArray.find(
      (item: any) => item.make === make && item.model === model,
    )
    return item?.settings?.customImg || null
  }

  return null // or handle the case when it's not an array
}

const goToVehiclePage = (make: string, model: string) => {
  const item = settings['filter'].find(
    (item: any) => item.make === make && item.model === model,
  )
  if (item?.settings.customUrl && item?.settings.customUrl.length > 0) {
    window.open('https://' + item.settings.customUrl, '_blank')
    return
  }
  if (settings.baseUrl.length === 0) return
  const url = buildUrl(make, model)
  window.open(url, '_blank')
}

const hideWhenZeroInStock = (make: string, model: string) => {
  const filterArray = settings.filter

  if (Array.isArray(filterArray)) {
    const item = filterArray.find(
      (item: any) => item.make === make && item.model === model,
    )
    if (
      (item && item.settings && item.settings.hideCountIfZero) ||
      (item && item.count !== 0)
    ) {
      return true
    } else {
      return false
    }
  }

  return false // Return empty string if 'filter' is not an array
}
const buildUrl = (make: string, model: string): string => {
  const prefix = settings.baseUrl.includes('https://') ? '' : 'https://'

  const structure = `/models-${make}-${model ? model.replace('-', '--') : ''}`

  return prefix + settings.baseUrl + structure
}

const disableUrl = (make: string, model: string, count: number) => {
  const filterArray = settings.filter

  if (Array.isArray(filterArray)) {
    const item = filterArray.find(
      (item: any) => item.make === make && item.model === model,
    )

    if (
      (item && item.settings.disableWhenCountIsZero && item.count === 0) ||
      (item &&
        item.settings &&
        !item.settings.hideCountIfZero &&
        item.count === 0)
    ) {
      return true
    }
  }

  return false
}

const getCustomCountText = (make: string, model: string) => {
  const filterArray = settings.filter

  if (Array.isArray(filterArray)) {
    const item = filterArray.find(
      (item: any) => item.make === make && item.model === model,
    )
    return item?.settings?.customCountText || ''
  }

  return '' // Return empty string if 'filter' is not an array
}
const sortVehicles = () => {
  if (settings.usedDrag) {
    return settings.filter
  }
  if (settings.sortType === 'asc') {
    return settings.filter.sort((a, b) => a.count - b.count)
  }
  if (settings.sortType === 'desc') {
    return settings.filter.sort((a, b) => b.count - a.count)
  }
  return settings.filter
}
</script>

<style scoped>
.jellybeans__card--link:hover .jellybeans__label {
  color: v-bind('settings.labelSetting.hoverColor') !important;
}

.carousel {
  padding: 40px;
}
</style>
